import { useEffect, useMemo, useState } from "react";
import { faCameraRetro } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import countryList from "react-select-country-list";
import { CircleLoader } from "../../components/CircleLoader";
import { uploadAccept } from "../../utilities/importantFns";

const relationshipArray = [
  "Father",
  "Mother",
  "Sister",
  "Brother",
  "Spouse",
  "Partner",
  "Others",
];

export const EditStaffForm = ({
  form,
  initialImage,
  handleImageUpload,
  handleChange,
  handleSubmit,
  isLoading,
}: any) => {
  const options = useMemo(() => countryList().getData(), []);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);

  useEffect(() => {
    // Whenever a new teacher is being edited, reset preview
    if (initialImage?.signature instanceof File) {
      const objectUrl = URL.createObjectURL(initialImage.signature);
      setSignaturePreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (form.signature && typeof form.signature === "string") {
      setSignaturePreview(form.signature);
    } else {
      setSignaturePreview(null);
    }
  }, [initialImage?.signature, form.signature]);

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-8 p-1 text-left max-w-5xl mx-auto"
    >
      {/* SECTION 1: Personal Parameters */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 pb-1 border-b border-slate-100">
          Personal Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              First Name <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              placeholder="First name"
              name="firstname"
              value={form?.firstname || ""}
              onChange={(e: any) => handleChange(e)}
              required
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Last Name / Surname <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Surname"
              name="surname"
              value={form?.surname || ""}
              onChange={(e: any) => handleChange(e)}
              required
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Other Names
            </label>
            <input
              type="text"
              placeholder="Other names"
              name="othername"
              value={form.othername || ""}
              onChange={(e: any) => handleChange(e)}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Email Reference{" "}
              <span className="text-slate-400 text-[10px] font-normal">
                (Read-only)
              </span>
            </label>
            <input
              type="email"
              placeholder="Email"
              name="email"
              value={form.email || ""}
              onChange={(e: any) => handleChange(e)}
              required
              readOnly
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-400 bg-slate-50 cursor-not-allowed outline-none"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Gender <span className="text-rose-500">*</span>
            </label>
            <div className="flex items-center gap-6 h-9 rounded-xl border border-slate-200 px-4 bg-slate-50/30">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer font-medium">
                <input
                  type="radio"
                  name="gender"
                  value="male"
                  checked={form.gender === "male"}
                  onChange={(e: any) => handleChange(e)}
                  required
                  className="w-4 h-4 text-[#0096D1] border-slate-300 focus:ring-[#0096D1]"
                />
                Male
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer font-medium">
                <input
                  type="radio"
                  name="gender"
                  value="female"
                  checked={form.gender === "female"}
                  onChange={(e: any) => handleChange(e)}
                  required
                  className="w-4 h-4 text-[#0096D1] border-slate-300 focus:ring-[#0096D1]"
                />
                Female
              </label>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Date of Birth <span className="text-rose-500">*</span>
            </label>
            <input
              type="date"
              name="date_of_birth"
              value={form.date_of_birth || ""}
              onChange={(e: any) => handleChange(e)}
              required
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Mobile Number <span className="text-rose-500">*</span>
            </label>
            <input
              type="tel"
              placeholder="Phone Number"
              name="phone_number"
              value={form.phone_number || ""}
              onChange={(e: any) => handleChange(e)}
              required
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Academic Qualification <span className="text-rose-500">*</span>
            </label>
            <select
              name="degree"
              value={form.degree || ""}
              onChange={(e: any) => handleChange(e)}
              required
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[right_0.875rem_center] bg-no-repeat pr-10"
            >
              <option value="">Select qualification</option>
              <option value="Graduate">Graduate</option>
              <option value="Masters">Masters</option>
              <option value="Postgraduate Diploma">Postgraduate Diploma</option>
              <option value="PhD">PhD</option>
              <option value="Other Qualifications">Other Qualifications</option>
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Discipline Field <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Discipline Name"
              name="discipline"
              value={form.discipline || ""}
              onChange={(e: any) => handleChange(e)}
              required
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>
        </div>
      </div>

      {/* SECTION 2: Address Geography */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 pb-1 border-b border-slate-100">
          Geographical Address Address
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div className="flex flex-col gap-1.5 md:col-span-2 lg:col-span-1">
            <label className="text-xs font-bold text-slate-700">
              Primary Street Address <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              placeholder="House address"
              name="house_address"
              value={form.house_address || ""}
              onChange={(e: any) => handleChange(e)}
              required
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              City Location <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              placeholder="City"
              name="city"
              value={form.city || ""}
              required
              onChange={(e: any) => handleChange(e)}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              State / Region <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              placeholder="State"
              name="state"
              value={form.state || ""}
              onChange={(e: any) => handleChange(e)}
              required
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Country Jurisdiction <span className="text-rose-500">*</span>
            </label>
            <select
              name="country"
              value={form.country || ""}
              onChange={(e: any) => handleChange(e)}
              required
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[right_0.875rem_center] bg-no-repeat pr-10"
            >
              <option value="">Select Country</option>
              {options?.map((country: any) => (
                <option key={country.label} value={country.label}>
                  {country.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Zip / Postal Code
            </label>
            <input
              type="text"
              placeholder="Zipcode"
              name="zip_code"
              value={form.zip_code || ""}
              onChange={(e: any) => handleChange(e)}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>

          <div className="flex flex-col gap-1.5 md:col-span-2">
            <label className="text-xs font-bold text-slate-700">
              Alternative Address Line 2
            </label>
            <input
              type="text"
              placeholder="Suite, unit, building, secondary layout configurations..."
              name="house_address2"
              value={form.house_address2 || ""}
              onChange={(e: any) => handleChange(e)}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>
        </div>
      </div>

      {/* SECTION 3: Emergency Contingencies */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 pb-1 border-b border-slate-100">
          Emergency Contact Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Contact Full Name
            </label>
            <input
              type="text"
              placeholder="Emergency contact full name"
              name="emergency_contact_name"
              value={form.emergency_contact_name || ""}
              onChange={(e: any) => handleChange(e)}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Telephone Line
            </label>
            <input
              type="text"
              placeholder="Phone number"
              name="emergency_contact_phone"
              value={form.emergency_contact_phone || ""}
              onChange={(e: any) => handleChange(e)}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Relationship Type
            </label>
            <select
              name="emergency_contact_relationship"
              value={form.emergency_contact_relationship || ""}
              onChange={(e: any) => handleChange(e)}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/30 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[right_0.875rem_center] bg-no-repeat pr-10"
            >
              <option value="">Select Relationship</option>
              {relationshipArray.map((item, idx: number) => (
                <option key={idx} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* SECTION 4: Media Upload Attachments */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 pb-1 border-b border-slate-100">
          Media Attachments & Verification
        </h3>
        <div className="flex flex-col md:flex-row gap-8 justify-start items-start pt-2">
          {/* Profile Picture Controller Grid Dropzone */}
          <div className="w-full md:w-1/2 flex flex-col gap-2">
            <span className="text-xs font-bold text-slate-700">
              Profile Representation Picture
            </span>
            <label className="relative flex flex-col items-center justify-center w-full h-44 rounded-2xl border-2 border-dashed border-slate-200 hover:border-[#0096D1] bg-slate-50/50 hover:bg-slate-50 cursor-pointer transition-all overflow-hidden group">
              <input
                type="file"
                name="profile_picture"
                accept={uploadAccept.image}
                onChange={(e: any) => handleImageUpload(e)}
                className="hidden"
              />
              {form.profile_picture || initialImage?.profile_picture ? (
                <div className="absolute inset-0 w-full h-full p-2 bg-white">
                  <img
                    className="w-full h-full object-cover rounded-xl border border-slate-100 shadow-sm"
                    src={
                      (initialImage?.profile_picture &&
                        URL.createObjectURL(initialImage?.profile_picture)) ||
                      form.profile_picture
                    }
                    alt="profile preview"
                  />
                  <div className="absolute inset-2 bg-black/40 text-white font-semibold text-xs opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-xl transition-opacity duration-200">
                    Change Picture
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-center p-4">
                  <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 mb-2 group-hover:bg-blue-50 group-hover:text-[#0096D1] transition-colors">
                    <FontAwesomeIcon icon={faCameraRetro} size="sm" />
                  </div>
                  <span className="text-xs font-bold text-slate-600">
                    Upload Image Frame
                  </span>
                  <span className="text-[10px] text-slate-400 mt-0.5 font-medium">
                    PNG, JPG, or GIF file structures
                  </span>
                </div>
              )}
            </label>
          </div>

          {/* Signature Verification Grid Dropzone */}
          <div className="w-full md:w-1/2 flex flex-col gap-2">
            <span className="text-xs font-bold text-slate-700">
              Teacher's Authorization Signature
            </span>
            <label className="relative flex flex-col items-center justify-center w-full h-44 rounded-2xl border-2 border-dashed border-slate-200 hover:border-[#0096D1] bg-slate-50/50 hover:bg-slate-50 cursor-pointer transition-all overflow-hidden group">
              <input
                id="signImage"
                type="file"
                name="signature"
                accept={uploadAccept.image}
                onChange={(e: any) => handleImageUpload(e)}
                className="hidden"
              />
              {form?.signature || initialImage?.signature ? (
                <div className="absolute inset-0 w-full h-full p-2 bg-white">
                  <img
                    className="w-full h-full object-contain bg-slate-50/40 rounded-xl border border-slate-100 p-4"
                    src={
                      (initialImage?.signature &&
                        URL.createObjectURL(initialImage?.signature)) ||
                      form.signature
                    }
                    alt="signature preview"
                  />
                  <div className="absolute inset-2 bg-black/40 text-white font-semibold text-xs opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-xl transition-opacity duration-200">
                    Change Signature
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-center p-4">
                  <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 mb-2 group-hover:bg-blue-50 group-hover:text-[#0096D1] transition-colors">
                    <FontAwesomeIcon icon={faCameraRetro} size="sm" />
                  </div>
                  <span className="text-xs font-bold text-slate-600">
                    Upload Signature Matrix
                  </span>
                  <span className="text-[10px] text-slate-400 mt-0.5 font-medium">
                    Transparent PNG configurations preferred
                  </span>
                </div>
              )}
            </label>
          </div>
        </div>
      </div>

      {/* Structural Form Execution Button Submit Control */}
      <div className="w-full flex justify-end gap-3 pt-6 border-t border-slate-100 mt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="w-full sm:w-auto min-w-[180px] inline-flex items-center justify-center rounded-xl px-5 py-3 bg-[#012063] hover:bg-[#0096D1] disabled:bg-slate-400 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all transform active:scale-95"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <CircleLoader />
              <span>Saving Changes...</span>
            </div>
          ) : (
            "Save All Changes"
          )}
        </button>
      </div>
    </form>
  );
};
